// feature/frontend-stability.
// Top-level React error boundary so a render-time exception in any component
// renders a friendly recovery screen instead of a blank white page. Also
// fires a fire-and-forget POST to /api/client-errors so we get a server-side
// trail of which user/component blew up.

import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("Athena error boundary caught:", error, errorInfo);
    try {
      const token = (() => {
        try { return localStorage.getItem("eds.token"); } catch (_) { return null; }
      })();
      fetch("/api/client-errors", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: error?.message,
          stack: error?.stack,
          componentStack: errorInfo?.componentStack,
          url: typeof window !== "undefined" ? window.location.href : null,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        }),
      }).catch(() => { /* fire-and-forget */ });
    } catch (_) { /* never throw from here */ }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 32,
        textAlign: "center",
        fontFamily: "system-ui, -apple-system, sans-serif",
        background: "var(--bg-subtle)",
      }}>
        <h1 style={{ fontSize: 20, marginBottom: 8, color: "var(--accent-ink)" }}>
          Algo salió mal
        </h1>
        <p style={{ color: "var(--text-muted)", marginBottom: 24, maxWidth: 480 }}>
          La aplicación encontró un error. Esto a veces pasa después de actualizaciones —
          recarga la página para cargar la versión más reciente.
        </p>
        <button
          onClick={() => { window.location.reload(); }}
          style={{
            padding: "10px 20px",
            fontSize: 14,
            fontWeight: 600,
            background: "var(--accent-ink)",
            color: "var(--on-accent)",
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          Recargar página
        </button>
        {this.state.error?.message && (
          <details style={{ marginTop: 32, maxWidth: 600, color: "var(--text-hint)", fontSize: 11 }}>
            <summary style={{ cursor: "pointer" }}>Detalles técnicos</summary>
            <pre style={{
              textAlign: "left",
              marginTop: 8,
              padding: 12,
              background: "var(--bg-card)",
              borderRadius: 4,
              overflow: "auto",
              fontSize: 10,
            }}>
              {this.state.error.message}
              {"\n\n"}
              {this.state.error.stack}
            </pre>
          </details>
        )}
      </div>
    );
  }
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
